import * as yup from 'yup';
import { User, UserInviteFormValues, UserType, reverseRoleMapper, roleMapper } from '../../api/types';
import { Controller, ControllerRenderProps, SubmitHandler, useForm, useWatch } from 'react-hook-form';
import validator from 'validator';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import Button from '../../../../components/button';
import FormInput from '../../../../components/forms/form-input';
import FormInputPhone from '../../../../components/forms/form-input-phone';
import Dialog from '../../../../components/dialog';
import FormSelect from '../../../../components/forms/form-select';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
    useDeleteUserMutation,
    useInviteUserMutation,
    useLockUserMutation,
    useUnlockUserMutation,
    useUpdateUserMutation,
} from '../../api/userManagementApi';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { addNotification } from '../../../notification/slices/notificationSlice';
import { NotificationType } from '../../../notification/types';
import { Product } from '../../../products/api/types';
import Multiselect from '../../../../components/multiselect';
import ProductLabel from './product-label';
import { isApiError } from '../../../../baseQuery/utils';
import { i18n } from '@senderra/translations';
import { CONFLICT_ERROR } from '../../../../constants';
import ConfirmationDialog from '../../../../components/confirmation';

interface UserManagementDialogProps {
    user: User | null;
    visible: boolean;
    onClose: (redirectToFirstPage?: boolean) => void;
    products: Array<Product>;
}

const schema = yup.object().shape({
    firstName: yup.string().max(128, i18n.t('common-string-max-length')).required(i18n.t('first-name-required')),
    lastName: yup.string().max(128, i18n.t('common-string-max-length')).required(i18n.t('last-name-required')),
    phoneNumber: yup
        .string()
        .test('is-valid-phone', i18n.t('invalid-phone-number'), (value) => {
            if (!value) return true;
            return /^\+1\d{10}$/.test(value);
        })
        .default(''),
    title: yup.string().max(128, i18n.t('common-string-max-length')).default(''),
    email: yup
        .string()
        .required(i18n.t('email-required'))
        .test('is-valid-email', i18n.t('invalid-email'), (value) => (value ? validator.isEmail(value) : false)),
    type: yup.string().required(i18n.t('role-required')),
    products: yup.array(),
});

const UserManagementDialog = ({ user, visible, onClose, products }: UserManagementDialogProps) => {
    const dispatch = useDispatch();
    const [confirmationDialog, setConfirmationDialog] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [search, setSearch] = useState('');
    const [showRoleWarningDialog, setShowRoleWarningDialog] = useState(false);
    const previousRole = useRef<string | null>(null);

    const [localUser, setLocalUser] = useState<User | null>(user);

    const { t } = useTranslation();
    const [inviteUser] = useInviteUserMutation();
    const [updateUser] = useUpdateUserMutation();
    const [lockUser] = useLockUserMutation();
    const [unlockUser] = useUnlockUserMutation();
    const [deleteUser] = useDeleteUserMutation();

    const {
        register,
        handleSubmit,
        control,
        setValue,
        getValues,
        formState: { errors },
    } = useForm<UserInviteFormValues>({
        resolver: yupResolver(schema),
        defaultValues: {
            firstName: localUser?.firstName || '',
            lastName: localUser?.lastName || '',
            phoneNumber: localUser?.phoneNumber || '',
            title: localUser?.title || '',
            email: localUser?.email || '',
            type: localUser?.roleName ? reverseRoleMapper[localUser.roleName] : '',
            products: localUser?.products || [],
        },
    });

    const roles = Object.values(UserType)
        .filter((role) => role !== UserType.PlatformAdmin)
        .map((role) => ({
            value: role,
            label: roleMapper[role],
        }));

    const selectedRole = useWatch({ control, name: 'type' });

    const filteredProducts = useMemo(() => {
        return products.filter((product) => product.shortName.toLowerCase().includes(search.toLowerCase()));
    }, [search, products]);

    useEffect(() => {
        if (previousRole.current !== null && previousRole.current !== selectedRole) {
            setValue('products', []);
        }
        previousRole.current = selectedRole;
    }, [selectedRole, setValue]);

    const onSubmit: SubmitHandler<UserInviteFormValues> = async (data) => {
        if (data.type !== UserType.USER) {
            data.products = [];
        }

        const handleErrors = (error: unknown) => {
            if (isApiError(error) && error.status === CONFLICT_ERROR) {
                dispatch(addNotification({ message: t('email-already-exists'), type: NotificationType.ERROR }));
            } else {
                dispatch(addNotification({ message: t('update-error-notification'), type: NotificationType.ERROR }));
            }
        };

        try {
            if (localUser?.id) {
                if (reverseRoleMapper[localUser.roleName] === UserType.USER && selectedRole === UserType.ADMIN) {
                    setShowRoleWarningDialog(true);
                } else {
                    await updateUser({ ...data, id: localUser?.id }).unwrap();
                    dispatch(addNotification({ message: t('user-update-success'), type: NotificationType.SUCCESS }));
                    onClose(true);
                }
            } else {
                await inviteUser({ ...data }).unwrap();
                dispatch(addNotification({ message: t('user-invite-success'), type: NotificationType.SUCCESS }));
                onClose(true);
            }
        } catch (error) {
            handleErrors(error);
        }
    };

    const handleLock = async () => {
        if (localUser && !processing) {
            setProcessing(true);
            try {
                const updatedUser = await lockUser({ id: localUser.id }).unwrap();
                setLocalUser(updatedUser);
                dispatch(addNotification({ message: t('user-locked'), type: NotificationType.SUCCESS }));
            } catch (error) {
                dispatch(addNotification({ message: t('update-error-notification'), type: NotificationType.ERROR }));
            } finally {
                setProcessing(false);
            }
        }
    };

    const handleUnlock = async () => {
        if (localUser && !processing) {
            setProcessing(true);
            try {
                const updatedUser = await unlockUser({ id: localUser.id }).unwrap();
                setLocalUser(updatedUser);
                dispatch(addNotification({ message: t('user-unlocked'), type: NotificationType.SUCCESS }));
            } catch (error) {
                dispatch(addNotification({ message: t('update-error-notification'), type: NotificationType.ERROR }));
            } finally {
                setProcessing(false);
            }
        }
    };

    const handleDelete = () => {
        setConfirmationDialog(true);
    };

    const closeConfirmationDialog = () => {
        setConfirmationDialog(false);
    };

    const onDelete = async () => {
        if (localUser && !processing) {
            setProcessing(true);
            try {
                await deleteUser({ id: localUser.id }).unwrap();
                dispatch(addNotification({ message: t('user-deleted'), type: NotificationType.SUCCESS }));
                onClose();
                setConfirmationDialog(false);
            } catch (error) {
                dispatch(addNotification({ message: t('update-error-notification'), type: NotificationType.ERROR }));
            } finally {
                setProcessing(false);
            }
        }
    };

    const handleAdditionalDialogClose = async (confirm: boolean) => {
        setShowRoleWarningDialog(false);
        if (confirm && localUser) {
            const formData = getValues();
            try {
                await updateUser({ ...formData, id: localUser.id }).unwrap();
                dispatch(addNotification({ message: t('user-update-success'), type: NotificationType.SUCCESS }));
                onClose(true);
            } catch (error) {
                dispatch(addNotification({ message: t('update-error-notification'), type: NotificationType.ERROR }));
            }
        }
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
    };

    const clearAllProducts = (field: ControllerRenderProps<UserInviteFormValues, 'products'>) => {
        const selectedProducts = field.value
            ? field.value.filter((product) => !product.shortName.toLowerCase().includes(search.toLowerCase()))
            : [];

        field.onChange(selectedProducts);
    };

    const selectAllProducts = (field: ControllerRenderProps<UserInviteFormValues, 'products'>) => {
        const filteredNotSelected = filteredProducts.filter(
            (product) => !field.value?.some((value) => value.shortName === product.shortName)
        );

        field.onChange([...(field.value || []), ...filteredNotSelected]);
    };

    return (
        <Dialog
            isOpen={visible}
            title={localUser?.id ? t('edit-user') : t('invite-user')}
            onClose={onClose}
            fullScreenOnMobile
        >
            <Box
                component='form'
                onSubmit={handleSubmit(onSubmit)}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                    width: '552px',
                    '@media (max-width: 600px)': {
                        width: 'calc(100vw - 75px)',
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                        width: '552px',
                        '@media (max-width: 600px)': {
                            width: '100%',
                        },
                    }}
                >
                    <FormSelect
                        name='type'
                        label={`${t('role')}*`}
                        options={roles}
                        register={register}
                        error={!!errors.type}
                        helperText={errors.type?.message}
                        isDisabled={false}
                        size='large'
                        defaultValue={localUser?.roleName && reverseRoleMapper[localUser.roleName]}
                    />
                    <FormInput
                        name='firstName'
                        label={`${t('first-name')}*`}
                        register={register}
                        error={!!errors.firstName}
                        helperText={errors.firstName?.message}
                    />
                    <FormInput
                        name='lastName'
                        label={`${t('last-name')}*`}
                        register={register}
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message}
                    />
                    <FormInput
                        name='email'
                        label={`${t('email')}*`}
                        register={register}
                        error={!!errors.email}
                        helperText={errors.email?.message}
                    />
                    <FormInput
                        name='title'
                        label={t('title')}
                        register={register}
                        error={!!errors.title}
                        helperText={errors.title?.message}
                    />

                    <FormInputPhone
                        name='phoneNumber'
                        control={control}
                        label={`${t('phone-number')}`}
                        error={!!errors.phoneNumber}
                        helperText={errors.phoneNumber?.message}
                    />

                    {selectedRole === UserType.USER && (
                        <Controller
                            name='products'
                            control={control}
                            render={({ field }) => {
                                return (
                                    <Multiselect<Product>
                                        selectedItems={field.value ?? []}
                                        items={filteredProducts}
                                        handleItemChange={(event, item) => {
                                            const newValue = event.target.checked
                                                ? [...(field.value ?? []), item]
                                                : (field.value ?? []).filter(
                                                      (i: Product) => i.shortName !== item.shortName
                                                  );
                                            field.onChange(newValue);
                                        }}
                                        renderLabel={(product) => <ProductLabel product={product} />}
                                        itemIdentifier={(product) => `${product.shortName}`}
                                        label={`${t('assign-products')}`}
                                        fetchMoreData={() => null}
                                        id='productsMultiselect'
                                        error={!!errors.products}
                                        helperText={errors.products?.message}
                                        openToTop={true}
                                        searchPlaceholder={t('search-for-drugs')}
                                        handleSearchChange={handleSearchChange}
                                        search={search}
                                        clearAll={() => clearAllProducts(field)}
                                        selectAll={() => selectAllProducts(field)}
                                    />
                                );
                            }}
                        />
                    )}

                    {localUser?.id && localUser.isLocked && (
                        <Box>
                            <Typography variant='h4'>{t('unlock-user')}</Typography>
                            <Typography sx={{ color: 'common.grey1', fontSize: '14px' }}>
                                {t('lock-user-info')}
                            </Typography>
                            <Button
                                icon={LockPersonIcon}
                                label={t('unlock-user')}
                                testId='unlock-button'
                                size='large'
                                onClick={handleUnlock}
                                variant='success-text'
                            />
                        </Box>
                    )}
                    {localUser?.id && !localUser.isLocked && (
                        <Box>
                            <Typography variant='h4'>{t('lock-user')}</Typography>
                            <Typography sx={{ color: 'common.grey1', fontSize: '14px' }}>
                                {t('lock-user-info')}
                            </Typography>
                            <Button
                                icon={LockOpenIcon}
                                label={t('lock')}
                                testId='lock-button'
                                size='large'
                                onClick={handleLock}
                                variant='danger-text'
                            />
                        </Box>
                    )}

                    {localUser?.id && (
                        <Box>
                            <Typography variant='h4'>{t('delete-user')}</Typography>
                            <Typography sx={{ color: 'common.grey1', fontSize: '14px' }}>
                                {t('delete-user-info')}
                            </Typography>
                            <Button
                                icon={DeleteOutlineIcon}
                                label={t('delete')}
                                size='large'
                                testId='delete-button'
                                onClick={handleDelete}
                                variant='danger-text'
                            />
                        </Box>
                    )}
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        width: '100%',
                        marginTop: '24px',
                        gap: '16px',
                    }}
                >
                    <Button
                        label={t('cancel')}
                        size='large'
                        variant='outlined'
                        testId='cancel-button'
                        onClick={onClose}
                    />
                    <Button label={t('save')} size='large' variant='primary' testId='save-button' type='submit' />
                </Box>
            </Box>
            <ConfirmationDialog
                isOpen={confirmationDialog}
                onClose={closeConfirmationDialog}
                onConfirm={onDelete}
                description={t('delete-user-info')}
            />
            <ConfirmationDialog
                isOpen={showRoleWarningDialog}
                onClose={() => handleAdditionalDialogClose(false)}
                onConfirm={() => handleAdditionalDialogClose(true)}
                description={t('role-change-warning')}
                primary={true}
                confirmationTitle={t('ok')}
            />
        </Dialog>
    );
};

export default UserManagementDialog;
